/**
 * useCaseName
 *
 * Encapsulates all business logic for the CaseName component:
 * - Sidebar new-message badge
 * - Inline case-search / case-change flow
 * - Conflict detection (participants not linked to the new case)
 * - PDF download
 */

import { useCallback, useEffect, useState } from 'react';

import useCaseNoteDownload from 'hooks/useCaseNoteDownload';
import { getTenantDetails } from 'utils/utils';
import { useWebSocket } from 'context/WebSocketConnectionContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useChangeMessageCase } from './useMessageMutations';
import { getClientUserList } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';
import { queryClient } from 'config/queryClient';

// ── Helper ────────────────────────────────────────────────────────────────────

/**
 * Returns participants from the current thread that are NOT linked
 * to the newly selected case. Groups are always included.
 */
function computeCaseConflicts(participants, newCaseUsers) {
  const individuals = (participants || []).filter((one) => {
    if (one?.res_type === 'user-group') return false;
    const found = (newCaseUsers || []).find((two) => {
      if (one?.client_cognito_username) return one.client_cognito_username === two.client_cognito_username;
      return one?.contact_id && one.contact_id === two.contact_id;
    });
    return !found;
  });
  const groups = (participants || []).filter((one) => one?.res_type === 'user-group');
  return [...individuals, ...groups];
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * @param {{
 *   messageDetails: object|null,
 *   setShowInviteDialog: Function,
 *   setShowSideBar: Function,
 * }} params
 */
export function useCaseName({ messageDetails, setShowInviteDialog, setShowSideBar }) {
  const { downloadCaseNotesAsPDF } = useCaseNoteDownload();
  const { subscribe } = useWebSocket();
  const { addToast } = useToast();
  const changeMessageCaseMutation = useChangeMessageCase();

  // ── Sidebar badge ─────────────────────────────────────────────────────────
  const [newMsg, setNewMsg] = useState(messageDetails?.has_new_sidebar_messages === true);

  useEffect(() => {
    const unsubscribe = subscribe('sidebar_message', (data) => {
      if (data?.message_thread_id === messageDetails?.message_thread_id) setNewMsg(true);
    });
    return () => unsubscribe?.();
  }, [messageDetails?.message_thread_id, subscribe]);

  useEffect(() => {
    if (messageDetails?.has_new_sidebar_messages === true) setNewMsg(true);
  }, [messageDetails?.has_new_sidebar_messages]);

  // ── Case-search state ─────────────────────────────────────────────────────
  const [showCaseSearch, setShowCaseSearch] = useState(false);
  const [selectedCase, setSelectedCase] = useState('');
  const [caseError, setCaseError] = useState('');
  const [newCaseUsers, setNewCaseUsers] = useState([]);
  const [isLoadingCaseUsers, setIsLoadingCaseUsers] = useState(false);

  // ── Conflict-dialog state ─────────────────────────────────────────────────
  const [conflictUsers, setConflictUsers] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const resetCaseSearch = useCallback(() => {
    setShowCaseSearch(false);
    setSelectedCase('');
    setNewCaseUsers([]);
    setIsLoadingCaseUsers(false);
    setCaseError('');
    setShowConflictDialog(false);
    setConflictUsers([]);
  }, []);

  const handleEditCase = useCallback(() => {
    setSelectedCase('');
    setCaseError('');
    setShowCaseSearch(true);
  }, []);

  const handleCaseDropdownChange = useCallback((e) => {
    setCaseError('');
    setSelectedCase(e);
    if (!e?.id) {
      setNewCaseUsers([]);
      setIsLoadingCaseUsers(false);
      return;
    }
    setIsLoadingCaseUsers(true);
    setNewCaseUsers([]);
    getClientUserList({
      caseId: e.id,
      is_include_groups: true,
      active_groups: true,
      excludeFromLoading: true,
    })
      .then((res) => setNewCaseUsers(res?.data || []))
      .catch(() => setNewCaseUsers([]))
      .finally(() => setIsLoadingCaseUsers(false));
  }, []);

  /** Performs the actual PATCH API call to change the case. */
  const doChangeCase = useCallback(() => {
    const [tenantId] = getTenantDetails();
    changeMessageCaseMutation.mutate(
      {
        pk: `${tenantId}#${messageDetails?.message_thread_id}`,
        data: {
          case_id: selectedCase?.id,
          case_name: selectedCase?.case_name || '',
        },
      },
      {
        onSuccess: () => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CASE_UPDATED_CREATED);
          resetCaseSearch();
          queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadDetails.byId(messageDetails?.message_thread_id) });
        },
        onError: (err) => {
          const msg = err?.response?.data ?? toastConstant.message.CASE_UPDATED_FAILED;
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
          resetCaseSearch();
        },
      }
    );
  }, [selectedCase, messageDetails?.message_thread_id]);

  /** Validates the selection then either shows conflict dialog or confirms directly. */
  const handleConfirmCaseChange = useCallback(() => {
    if (!selectedCase?.id) {
      setCaseError('Please select a case');
      return;
    }
    if (isLoadingCaseUsers) return;
    setCaseError('');

    const participants = [...(messageDetails?.firm_users || []), ...(messageDetails?.contacts || []), ...(messageDetails?.groups || [])];
    const conflicts = computeCaseConflicts(participants, newCaseUsers);

    if (conflicts.length > 0) {
      setConflictUsers(conflicts);
      setShowConflictDialog(true);
      return;
    }

    doChangeCase();
  }, [selectedCase, isLoadingCaseUsers, messageDetails]);

  const handleConflictDialogHide = useCallback(() => {
    setShowConflictDialog(false);
    setConflictUsers([]);
  }, []);

  /** Opens the invite dialog, pre-loading case contacts. */
  const handleInviteClick = useCallback(() => {
    setShowInviteDialog(true);
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setShowSideBar((prev) => !prev);
    setNewMsg(false);
  }, [setShowSideBar]);

  /** Downloads the thread as a PDF. */
  const downloadSecureMessagePDF = useCallback(() => {
    const [tenantId] = getTenantDetails();
    if (messageDetails?.case?.case_id) {
      downloadCaseNotesAsPDF(messageDetails.case.case_id, {
        case_name: messageDetails?.case?.case_name || 'Untitled',
        download_type: 'secure_message',
        message_thread_pk: `${tenantId}#${messageDetails?.message_thread_id}`,
      });
    }
  }, [messageDetails?.message_thread_id, messageDetails.case]);

  return {
    // Sidebar badge
    newMsg,
    // Case-search UI
    showCaseSearch,
    selectedCase,
    caseError,
    isLoadingCaseUsers,
    // Conflict dialog
    conflictUsers,
    showConflictDialog,
    isMutating: changeMessageCaseMutation.isLoading,
    // Handlers
    handleEditCase,
    handleCaseDropdownChange,
    resetCaseSearch,
    handleConfirmCaseChange,
    doChangeCase,
    handleConflictDialogHide,
    handleInviteClick,
    handleSidebarToggle,
    downloadSecureMessagePDF,
  };
}
